export const ANALYTICS_CONSENT_STORAGE_KEY = 'tcg-calendar-analytics-consent-v1';
export const ANALYTICS_CONSENT_CHANGED_EVENT = 'tcg-calendar-analytics-consent-changed';

export type AnalyticsConsent = 'unknown' | 'granted' | 'denied';
export type AnalyticsConsentDecision = Exclude<AnalyticsConsent, 'unknown'>;

export interface AnalyticsConsentChange {
  consent: AnalyticsConsent;
  persisted: boolean;
}

let sessionConsent: AnalyticsConsent = 'unknown';
let sessionConsentPersisted = false;
let sessionChoiceOverridesStorage = false;

const isConsent = (value: unknown): value is AnalyticsConsentDecision => (
  value === 'granted' || value === 'denied'
);

const emitConsentChange = (detail: AnalyticsConsentChange) => {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;

  window.dispatchEvent(new window.CustomEvent<AnalyticsConsentChange>(
    ANALYTICS_CONSENT_CHANGED_EVENT,
    { detail }
  ));
};

/**
 * Reads the optional analytics choice for this browser. Storage denial is a
 * supported state: a choice made during the current page session still works,
 * but a later visit will ask again rather than silently assuming consent.
 */
export const getAnalyticsConsent = (): AnalyticsConsent => {
  if (typeof window === 'undefined') return sessionConsent;
  if (sessionChoiceOverridesStorage) return sessionConsent;

  try {
    const stored = window.localStorage.getItem(ANALYTICS_CONSENT_STORAGE_KEY);
    if (isConsent(stored)) {
      sessionConsent = stored;
      sessionConsentPersisted = true;
      return stored;
    }

    sessionConsent = 'unknown';
    sessionConsentPersisted = false;
  } catch {
    // Keep the in-memory choice for this page session when storage is blocked.
  }

  return sessionConsent;
};

export const isAnalyticsConsentPersisted = (): boolean => {
  getAnalyticsConsent();
  return sessionConsentPersisted;
};

/**
 * Saves a deliberate Allow or Decline choice. The return value tells the UI
 * whether it will survive a reload. The internal change event is intentionally
 * provider-neutral; analytics adapters decide how to react to it.
 */
export const setAnalyticsConsent = (consent: AnalyticsConsentDecision): boolean => {
  sessionConsent = consent;
  sessionConsentPersisted = false;
  sessionChoiceOverridesStorage = true;

  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(ANALYTICS_CONSENT_STORAGE_KEY, consent);
      sessionConsentPersisted = (
        window.localStorage.getItem(ANALYTICS_CONSENT_STORAGE_KEY) === consent
      );
      sessionChoiceOverridesStorage = !sessionConsentPersisted;

      if (!sessionConsentPersisted) {
        // Do not leave an older, opposite permission in durable storage when
        // verification fails. Unknown on the next visit is safer than reviving
        // a permission the visitor just changed.
        window.localStorage.removeItem(ANALYTICS_CONSENT_STORAGE_KEY);
      }
    } catch {
      sessionConsentPersisted = false;
      try {
        window.localStorage.removeItem(ANALYTICS_CONSENT_STORAGE_KEY);
      } catch {
        // Storage is entirely unavailable; the in-memory decision still wins.
      }
    }
  }

  emitConsentChange({ consent, persisted: sessionConsentPersisted });
  return sessionConsentPersisted;
};

/** Clears the saved choice so the browser will ask again. */
export const clearAnalyticsConsent = (): boolean => {
  sessionConsent = 'unknown';
  sessionConsentPersisted = false;
  sessionChoiceOverridesStorage = true;
  let cleared = typeof window === 'undefined';

  if (typeof window !== 'undefined') {
    try {
      window.localStorage.removeItem(ANALYTICS_CONSENT_STORAGE_KEY);
      cleared = window.localStorage.getItem(ANALYTICS_CONSENT_STORAGE_KEY) === null;
      sessionChoiceOverridesStorage = !cleared;
    } catch {
      cleared = false;
    }
  }

  emitConsentChange({ consent: 'unknown', persisted: false });
  return cleared;
};

export const subscribeAnalyticsConsent = (
  listener: (consent: AnalyticsConsent, change: AnalyticsConsentChange) => void
): (() => void) => {
  if (typeof window === 'undefined') return () => undefined;

  const handleConsentChange = (event: Event) => {
    const detail = (event as CustomEvent<AnalyticsConsentChange>).detail;
    if (!detail || !['unknown', 'granted', 'denied'].includes(detail.consent)) return;
    listener(detail.consent, detail);
  };

  const handleStorage = (event: StorageEvent) => {
    if (event.key !== ANALYTICS_CONSENT_STORAGE_KEY) return;

    sessionConsent = isConsent(event.newValue) ? event.newValue : 'unknown';
    sessionConsentPersisted = isConsent(event.newValue);
    sessionChoiceOverridesStorage = false;
    const change = { consent: sessionConsent, persisted: sessionConsentPersisted };
    listener(sessionConsent, change);
  };

  window.addEventListener(ANALYTICS_CONSENT_CHANGED_EVENT, handleConsentChange);
  window.addEventListener('storage', handleStorage);

  return () => {
    window.removeEventListener(ANALYTICS_CONSENT_CHANGED_EVENT, handleConsentChange);
    window.removeEventListener('storage', handleStorage);
  };
};
